import countySources from "../tn_county_election_sources_2026.json";
import novemberBallots from "./tnNovember32026Ballots.json";

// November data is deliberately separate from the August primary snapshot.
export const NOVEMBER_ELECTION = {
  date: "2026-11-03",
  label: "November 3, 2026",
  earlyStart: "2026-10-14",
  earlyEnd: "2026-10-29",
  registrationDeadline: "2026-10-05",
  absenteeRequestDeadline: "2026-10-24",
  checked: "2026-10-06",
  calendarUrl: "https://elect.hamiltontn.gov/keydates.aspx",
};

export const VOTER_LINKS = {
  lookup: "https://web.go-vote-tn.elections.tn.gov/",
  directory: "https://tnsosorg.tnsosfiles.com/elections/election_commissions.php?Show=All",
  absentee: "https://sos.tn.gov/products/elections/absentee-voting",
  photoId: "https://sos.tn.gov/products/elections/what-id-required-when-voting",
  amendments: "https://sos.tn.gov/elections/announcements/2026-proposed-constitutional-amendments",
  elections: "https://sos.tn.gov/elections",
};

const DAVIDSON_BALLOT = "https://www.nashville.gov/sites/default/files/2026-09/Sample_Ballot_November_3_2026_State_Fed_Gen_Mun_Elections.pdf?ct=1790110765";

// Only links checked against a November source belong in this map.
export const NOVEMBER_COUNTY_RESOURCES = {
  knox: {
    commissionUrl: "https://knoxcounty.org/election/",
    ballotUrl: "https://knoxcounty.org/election/pdfs/Revised_KnoxCo-SampleBallot_110326.pdf",
    earlyVotingUrl: "https://knoxcounty.org/election/pdfs/November32026EVDaysHours.pdf",
    earlyLocationsUrl: "https://knoxcounty.org/election/pdfs/November32026EVLocations.pdf",
    checked: "2026-10-06",
  },
  davidson: {
    commissionUrl: "https://www.nashville.gov/departments/elections/voters/find-2026-election-information",
    ballotUrl: DAVIDSON_BALLOT,
    earlyVotingUrl: "https://www.nashville.gov/sites/default/files/2026-08/Early_Voting_Schedule_NOV_3_2026_State_Fed_Gen_BMeade_FHills_Gville_Mun.pdf?ct=1787235029",
    checked: "2026-10-06",
  },
  shelby: {
    commissionUrl: "https://www.electionsshelbytn.gov/upcoming-elections/",
    ballotUrl: "https://www.electionsshelbytn.gov/wp-content/uploads/2026/09/Nov-3-2026-Official-Sample-2026-State-and-Federal-General-Election-and-Municipal-Elections-Paper-Ver-2.pdf",
    earlyVotingUrl: "https://www.electionsshelbytn.gov/wp-content/uploads/2026/09/EV-Locations-11.3.26-Election-with-hours-8.5X11-9.17.26.pdf",
    checked: "2026-10-06",
  },
  hamilton: {
    commissionUrl: "https://elect.hamiltontn.gov/",
    ballotUrl: "https://elect.hamiltontn.gov/sampleballots.aspx",
    earlyVotingUrl: "https://elect.hamiltontn.gov/ev.aspx",
    checked: "2026-10-06",
  },
};

export const NOVEMBER_COUNTIES = Object.entries(countySources)
  .map(([slug, source]) => ({
    slug,
    name: source.county_name,
    commissionUrl: source.commission_url || VOTER_LINKS.directory,
    ...(novemberBallots.counties[slug] ? {
      ballotUrl: novemberBallots.counties[slug].sources[0].url,
      ballotChecked: novemberBallots.checked,
    } : {}),
    ...NOVEMBER_COUNTY_RESOURCES[slug],
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const getNovemberCounty = (slug) => NOVEMBER_COUNTIES.find((county) => county.slug === slug);

const candidate = (name, party) => ({ name, party });

// Printed candidates, in official ballot order. Write-in instructions remain on the ballot.
export const STATEWIDE_RACES = [
  {
    id: "governor",
    title: "Tennessee Governor",
    description: "Leads the state executive branch and proposes the state budget.",
    sourceUrl: DAVIDSON_BALLOT,
    sourcePage: 2,
    candidates: [
      candidate("Marsha Blackburn", "Republican"),
      candidate("Jerri Green", "Democratic"),
      ...["Misam Abidi", "Dean Brewer", "Ray Brown", "David Hatley", "Wendell Jackson", "Charles Van Morgan", "Eddie Lee Murphy", "Lauren Pinkston", "Victor L. Scoggin", "Dave Seeman", "Karl Knox Smithson", "L. Webb Taylor", "Robert C. Vick"].map((name) => candidate(name, "Independent")),
    ],
  },
  {
    id: "us-senate",
    title: "U.S. Senate",
    description: "Represents Tennessee in the U.S. Senate on federal laws, spending, and confirmations.",
    sourceUrl: DAVIDSON_BALLOT,
    sourcePage: 5,
    candidates: [
      candidate("Bill Hagerty", "Republican"),
      candidate("Marquita Bradshaw", "Democratic"),
      ...["Tharon Chandler", "Andrew Gerena", "Jeremy Dean Hearn", "Robert Jones", "James William Macon III", "Yoshi D. Matthews", "David Sutman, Jr.", "Catherine Barcel “Barcy” Whitson"].map((name) => candidate(name, "Independent")),
    ],
  },
];

// An explicit timezone keeps date-only deadlines stable for visitors outside Tennessee.
// This is a calendar status, never a claim that a polling place is open right now.
export const getElectionDateKey = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(now);
  const part = (type) => parts.find((item) => item.type === type).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
};

export const getElectionStatus = (today = getElectionDateKey()) => {
  const election = NOVEMBER_ELECTION;
  if (today > election.date) return {
    phase: "past", heading: "The November 3 election has ended",
    detail: "See official election sources for results and certification updates. This guide remains available for reference.",
  };
  if (today === election.date) return {
    phase: "election-day", heading: "Today is Election Day",
    detail: "Check your assigned polling place and its local hours before heading out.",
  };
  if (today > election.earlyEnd) return {
    phase: "between", heading: "Early voting has ended",
    detail: "Election Day is Tuesday, November 3. Confirm your assigned polling place and local hours.",
  };
  if (today >= election.earlyStart) return {
    phase: "early", heading: today === election.earlyEnd ? "Last day of the early voting period" : "Early voting is underway",
    detail: "The early voting period runs October 14–29. Locations and daily hours vary by county; check the schedule before you go.",
  };
  const days = Math.round((Date.parse(election.earlyStart) - Date.parse(today)) / 86400000);
  return {
    phase: "upcoming", heading: days === 1 ? "Early voting starts tomorrow" : `Early voting starts in ${days} days`,
    detail: "Plan ahead for October 14–29. Find your county’s early voting locations, hours, and November sample ballot below.",
  };
};

export const getDeadlineStatus = (deadline, today = getElectionDateKey()) =>
  today > deadline ? "Deadline passed" : today === deadline ? "Due today — check local cutoff" : "Upcoming";
